import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  ArrowRight,
  Check,
  ChevronRight,
  Play,
  Star,
  Quote,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../components/ui/accordion";
import { cn } from "../../../lib/utils";
import {
  heroSection,
  challenges,
  solutionFeatures,
  moduleFeatures,
  roles,
  pricingPlans,
  registrationSteps,
  faqs,
  benefits,
} from "./EduAdminHubData";

const EduAdminHub = () => {
  const [activeModule, setActiveModule] = useState(moduleFeatures.modules[0].id);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 100,
    });
  }, []);

  const activeModuleData = moduleFeatures.modules.find(
    (m) => m.id === activeModule
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 lg:py-32 bg-gradient-to-br from-background via-primary/5 to-background border-b overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)`,
              backgroundSize: "50px 50px",
            }}
          />
        </div>

        {/* Animated Gradient Orbs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-30 animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-30 animate-pulse delay-1000" />

        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8" data-aos="fade-right" data-aos-delay="100">
              <div className="inline-block">
                <span className="text-sm font-medium text-primary bg-primary/10 px-4 py-2 rounded-full border border-primary/20">
                  {heroSection.badge}
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                {heroSection.title}:{" "}
                <span className="text-primary bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                  {heroSection.highlightedText}
                </span>
              </h1>

              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl">
                {heroSection.description}
              </p>

              <div className="flex flex-wrap gap-4">
                <Button size="lg" className="group">
                  {heroSection.cta.primary}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button size="lg" variant="outline" className="group">
                  <Play className="mr-2 h-4 w-4" />
                  {heroSection.cta.secondary}
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8">
                {heroSection.stats.map((stat, index) => (
                  <div
                    key={index}
                    className="text-center"
                    data-aos="fade-up"
                    data-aos-delay={200 + index * 100}
                  >
                    <div className="text-2xl md:text-3xl font-bold text-primary mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative" data-aos="fade-left" data-aos-delay="200">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-border">
                <div className="aspect-video bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                  <div className="text-center space-y-4">
                    <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto">
                      <Play className="h-10 w-10 text-primary ml-1" />
                    </div>
                    <p className="text-sm text-muted-foreground">Watch Demo Video</p>
                  </div>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="bg-green-500 text-white text-xs font-medium px-3 py-1 rounded-full flex items-center gap-2">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    Live Demo
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-20 md:py-24 bg-background">
        <div className="container">
          <div
            className="text-center max-w-3xl mx-auto mb-16"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {challenges.title}{" "}
              <span className="text-primary">{challenges.highlightedText}</span>
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4 mb-6" />
            <p className="text-lg text-muted-foreground leading-relaxed">
              {challenges.description}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {challenges.cards.map((card, index) => {
              const Icon = card.icon;
              return (
                <Card
                  key={index}
                  data-aos="fade-up"
                  data-aos-delay={100 + index * 100}
                  className={cn(
                    "border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl group",
                    "bg-gradient-to-br",
                    card.gradient
                  )}
                >
                  <CardHeader>
                    <div className="h-14 w-14 rounded-xl bg-background/80 backdrop-blur-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                      <Icon className={cn("h-7 w-7", card.iconColor)} />
                    </div>
                    <CardTitle className="text-xl mb-2">{card.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed">
                      {card.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solution Features Section */}
      <section className="py-20 md:py-24 bg-muted/30">
        <div className="container">
          <div
            className="text-center max-w-3xl mx-auto mb-16"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {solutionFeatures.title}{" "}
              <span className="text-primary">{solutionFeatures.highlightedText}</span>
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4 mb-6" />
            <p className="text-lg text-muted-foreground leading-relaxed">
              {solutionFeatures.description}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {solutionFeatures.features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card
                  key={index}
                  data-aos="fade-up"
                  data-aos-delay={100 + index * 100}
                  className={cn(
                    "border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl group relative overflow-hidden",
                    "bg-gradient-to-br",
                    feature.gradient
                  )}
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl -mr-16 -mt-16" />
                  <CardHeader className="relative z-10">
                    <div className="h-14 w-14 rounded-xl bg-background/80 backdrop-blur-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                      <Icon className={cn("h-7 w-7", feature.iconColor)} />
                    </div>
                    <CardTitle className="text-xl mb-2">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="relative z-10">
                    <p className="text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Module Features Section */}
      <section className="py-20 md:py-24 bg-background">
        <div className="container">
          <div
            className="text-center max-w-3xl mx-auto mb-16"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {moduleFeatures.title}{" "}
              <span className="text-primary">{moduleFeatures.highlightedText}</span>
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4 mb-6" />
            <p className="text-lg text-muted-foreground leading-relaxed">
              {moduleFeatures.description}
            </p>
          </div>

          <Tabs
            value={activeModule}
            onValueChange={setActiveModule}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-3 md:grid-cols-5 lg:grid-cols-9 h-auto p-1 mb-12 bg-muted/50">
              {moduleFeatures.modules.map((module) => {
                const Icon = module.icon;
                return (
                  <TabsTrigger
                    key={module.id}
                    value={module.id}
                    className="flex flex-col items-center gap-2 py-4 data-[state=active]:bg-background data-[state=active]:shadow-md"
                    data-aos="fade-up"
                    data-aos-delay={moduleFeatures.modules.indexOf(module) * 50}
                  >
                    <Icon className={cn("h-5 w-5", module.color)} />
                    <span className="text-xs text-center">{module.title}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {moduleFeatures.modules.map((module) => (
              <TabsContent
                key={module.id}
                value={module.id}
                className="mt-8"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                <Card className="border-border shadow-xl overflow-hidden">
                  <div className={cn("bg-gradient-to-br p-8", module.bgGradient)}>
                    <div className="grid md:grid-cols-2 gap-8 items-center">
                      <div className="space-y-6">
                        <div className="flex items-center gap-4">
                          <div
                            className={cn(
                              "h-16 w-16 rounded-xl bg-background/80 backdrop-blur-sm flex items-center justify-center shadow-lg"
                            )}
                          >
                            {React.createElement(module.icon, {
                              className: cn("h-8 w-8", module.color),
                            })}
                          </div>
                          <div>
                            <h3 className="text-2xl md:text-3xl font-bold">
                              {module.title}
                            </h3>
                            <p className="text-muted-foreground mt-1">
                              {module.description}
                            </p>
                          </div>
                        </div>

                        <ul className="space-y-3">
                          {module.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                              <span className="text-muted-foreground">{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <p className="text-muted-foreground leading-relaxed">
                          {module.details}
                        </p>
                      </div>

                      <div className="relative">
                        <div className="aspect-video rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border border-border">
                          <div className="text-center space-y-4">
                            <div
                              className={cn(
                                "w-20 h-20 rounded-full flex items-center justify-center mx-auto",
                                module.bgGradient,
                                "border-2 border-border"
                              )}
                            >
                              {React.createElement(module.icon, {
                                className: cn("h-10 w-10", module.color),
                              })}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {module.title} Module Preview
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Roles Section */}
      <section className="py-20 md:py-24 bg-muted/30">
        <div className="container">
          <div
            className="text-center max-w-3xl mx-auto mb-16"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {roles.title}{" "}
              <span className="text-primary">{roles.highlightedText}</span>
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4 mb-6" />
            <p className="text-lg text-muted-foreground leading-relaxed">
              {roles.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {roles.roles.map((role, index) => {
              const Icon = role.icon;
              return (
                <Card
                  key={index}
                  data-aos="fade-up"
                  data-aos-delay={100 + index * 100}
                  className={cn(
                    "border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl group text-center",
                    "bg-gradient-to-br",
                    role.gradient
                  )}
                >
                  <CardHeader>
                    <div className="h-20 w-20 rounded-2xl bg-background/80 backdrop-blur-sm flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg">
                      <Icon className={cn("h-10 w-10", role.iconColor)} />
                    </div>
                    <CardTitle className="text-xl mb-2">{role.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed">
                      {role.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits/Stats Section */}
      <section className="py-20 md:py-24 bg-background">
        <div className="container">
          <div
            className="text-center max-w-3xl mx-auto mb-16"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {benefits.title}{" "}
              <span className="text-primary">{benefits.highlightedText}</span>
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Card
                  key={index}
                  data-aos="fade-up"
                  data-aos-delay={100 + index * 100}
                  className="border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl text-center group"
                >
                  <CardHeader>
                    <div className="h-16 w-16 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                      <Icon className={cn("h-8 w-8", stat.color)} />
                    </div>
                    <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                      {stat.value}
                    </div>
                    <CardTitle className="text-lg mb-1">{stat.label}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{stat.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 md:py-24 bg-muted/30">
        <div className="container">
          <div
            className="text-center max-w-3xl mx-auto mb-16"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {pricingPlans.title}{" "}
              <span className="text-primary">{pricingPlans.highlightedText}</span>
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4 mb-6" />
            <p className="text-lg text-muted-foreground leading-relaxed">
              {pricingPlans.description}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.plans.map((plan, index) => (
              <Card
                key={index}
                data-aos="fade-up"
                data-aos-delay={100 + index * 100}
                className={cn(
                  "border-border hover:shadow-xl transition-all duration-300 relative overflow-hidden",
                  plan.popular && "border-primary border-2 shadow-lg scale-105"
                )}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-medium px-4 py-1 rounded-bl-lg">
                    Most Popular
                  </div>
                )}
                <div className={cn("bg-gradient-to-br p-8", plan.gradient)}>
                  <CardHeader>
                    <CardTitle className="text-2xl mb-2">{plan.name}</CardTitle>
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-4xl font-bold">{plan.price}</span>
                      {plan.period !== "forever" && (
                        <span className="text-muted-foreground">/{plan.period}</span>
                      )}
                    </div>
                    <CardDescription>{plan.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <ul className="space-y-3">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      className="w-full"
                      size="lg"
                      variant={plan.popular ? "default" : "outline"}
                    >
                      {plan.cta}
                    </Button>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Steps Section */}
      <section className="py-20 md:py-24 bg-background">
        <div className="container">
          <div
            className="text-center max-w-3xl mx-auto mb-16"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {registrationSteps.title}{" "}
              <span className="text-primary">{registrationSteps.highlightedText}</span>
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4 mb-6" />
            <p className="text-lg text-muted-foreground leading-relaxed">
              {registrationSteps.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {registrationSteps.steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <Card
                  key={index}
                  data-aos="fade-up"
                  data-aos-delay={100 + index * 100}
                  className="border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl group"
                >
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <Icon className="h-7 w-7 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-primary mb-1">
                          Step {step.number}
                        </div>
                        <CardTitle className="text-xl mb-2">{step.title}</CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-24 bg-muted/30">
        <div className="container">
          <div
            className="text-center max-w-3xl mx-auto mb-16"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {faqs.title}{" "}
              <span className="text-primary">{faqs.highlightedText}</span>
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4 mb-6" />
            <p className="text-lg text-muted-foreground leading-relaxed">
              {faqs.description}
            </p>
          </div>

          <div className="max-w-3xl mx-auto" data-aos="fade-up" data-aos-delay="200">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.questions.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border-border rounded-lg px-6 bg-background"
                >
                  <AccordionTrigger className="text-left font-semibold hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-24 bg-gradient-to-br from-primary/10 via-primary/5 to-background border-t">
        <div className="container">
          <Card
            className="border-border shadow-2xl overflow-hidden"
            data-aos="zoom-in"
            data-aos-delay="100"
          >
            <div className="bg-gradient-to-br from-primary/20 to-primary/5 p-12 md:p-16">
              <div className="text-center max-w-3xl mx-auto space-y-6">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
                  Ready to Transform Your School Management?
                </h2>
                <p className="text-lg text-muted-foreground">
                  Join hundreds of schools already using EduAdminHub to streamline
                  their operations and focus on what matters most — education.
                </p>
                <div className="flex flex-wrap justify-center gap-4 pt-4">
                  <Button size="lg" className="group">
                    Schedule a Demo
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  <Button size="lg" variant="outline">
                    Start Free Trial
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default EduAdminHub;
